import { useEffect } from "react";

import { Card, Button, ConfigProvider, Tag, Descriptions } from "antd";

import { createForm, setValidateLanguage } from "@formily/core";
import { FormProvider, createSchemaField } from "@formily/react";

import locale from "antd/locale/nl_NL";
import "dayjs/locale/nl";

import {
  LicensePlate,
  RumbleWelcome,
  DatePicker,
  RumbleDamageSideSelector,
  RumbleCompaniesSelect,
  RumbleServicePartnerSelector,
  Select,
  TextArea,
  Input,
  Html,
  Address,
  RumbleStepButtons,
  RumbleImageUpload,
  RumbleActOfCessionExample,
  RumbleSignature,
  RumbleImageCapturePending,
  RumbleImageCaptureHtml,
} from "@rumble-it/common-components/components/Rumble";

import {
  Upload,
  Form,
  FormItem,
  NumberPicker,
  Password,
  FormStep,
  FormGrid,
} from "@formily/antd-v5";

import { getFormConfigurationsByType } from "../lib/queries/getFormConfigurationsByType";
import { getFormConfiguration } from "../lib/queries/getFormConfiguration";
import { LoadingState } from "../components/loading-state";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { getDossier } from "../lib/queries/getDossier";

const form = createForm();

const SchemaField = createSchemaField({
  components: {
    FormGrid,
    FormStep,
    FormItem,
    RumbleInput: Input,
    Input,
    NumberPicker,
    Password,
    RumbleSelect: Select,
    Select,
    RumbleLicensePlate: LicensePlate,
    LicensePlate,
    RumbleWelcome,
    RumbleDamageSideSelector,
    RumbleDatePicker: DatePicker,
    DatePicker,
    RumbleCompaniesSelect,
    RumbleServicePartnerSelector,
    Card,
    RumbleHtml: Html,
    Html,
    Upload,
    RumbleTextArea: TextArea,
    TextArea,
    Button,
    RumbleStepButtons,
    RumbleAddress: Address,
    Address,
    RumbleImageUpload,
    RumbleActOfCessionExample,
    RumbleSignature,
    RumbleImageCapturePending,
    Tag,
    Descriptions,
    RumbleImageCaptureHtml,
  },
});

setValidateLanguage("nl-NL");

type defaultFormSchemaKeys = "form" | "onSuccess"; // Default keys for form schema.

export interface IFormContainerProps extends Record<string, unknown> {
  formType: string; // The type of form, e.g. "repair-direct-generic-dealer-intake".
  formSchemaKey: defaultFormSchemaKeys | string; // e.g. "onSuccess" for success state, or any other key for active form.
}

export const FormContainerSuccess = (props: IFormContainerProps) => {
  const [searchParams] = useSearchParams();
  const dossierId = searchParams.get("dossierId");

  const dossierQuery = useQuery({
    queryKey: ["dossier", props.dossierId],
    queryFn: async () => {
      if (!dossierId) throw new Error("No dossierId provided");
      return getDossier({ dossierId: dossierId });
    },
  });

  const dossier = dossierQuery.data;

  useEffect(() => {
    if (dossier) {
      const imageCapturePending =
        dossier?.imageCaptureSents?.[0]?.soleraState === "Pending";
      const imageCaptureLink = dossier?.imageCaptureSents?.[0]?.fullSoleraLink;

      const initialFormData = {
        dossier,
        servicePartner: dossier.servicePartners[0],
        imageCapturePending,
        imageCaptureLink,
      };

      form.setInitialValues(initialFormData);
    }
  }, [dossier]);

  const schemasQuery = useQuery({
    queryKey: ["formConfigurations", props.formType],
    queryFn: async () => {
      const availableForms = await getFormConfigurationsByType(props.formType);
      if (!availableForms || availableForms.length === 0) {
        throw new Error(
          `No form configurations found for type "${props.formType}".`,
        );
      }
      if (availableForms.length !== 1) {
        throw new Error(
          `Expected exactly one form configuration for type "${props.formType}", but found ${availableForms.length}.`,
        );
      }
      return availableForms[0];
    },
  });

  const formId = schemasQuery.data?.id;

  const schemaQuery = useQuery({
    queryKey: ["formConfiguration", formId],
    queryFn: async () => getFormConfiguration(formId),
    enabled: !!formId,
  });

  const formConfiguration = schemaQuery.data?.formConfiguration;

  if (!props.formSchemaKey) {
    console.error("No formSchemaKey provided for FormContainerSuccess.");
    return (
      <LoadingState
        isPending={false}
        isError={true}
        errorMessage="Formulier kon niet worden geladen.  Probeer het later opnieuw of neem contact op met de klantenservice."
      />
    );
  }

  if (schemaQuery.isPending || schemasQuery.isError) {
    return (
      <LoadingState
        isPending={schemaQuery.isPending}
        isError={schemaQuery.isError}
        errorMessage="Het formulier kon niet worden geladen. Probeer het later opnieuw of neem contact op met de klantenservice."
      />
    );
  }

  if (dossierQuery.isPending || dossierQuery.isError) {
    return (
      <LoadingState
        isPending={schemaQuery.isPending}
        isError={schemaQuery.isError || schemasQuery.isError}
        errorMessage="Het dossier kon niet worden geladen. Probeer het later opnieuw of neem contact op met de klantenservice."
      />
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      <ConfigProvider theme={formConfiguration.settings.theme} locale={locale}>
        <div
          id="rumbledirect-form"
          style={{ width: "100%", maxWidth: "800px" }}
        >
          <FormProvider form={form}>
            <Form layout={"vertical"}>
              <SchemaField schema={formConfiguration[props.formSchemaKey]} />
            </Form>
          </FormProvider>
        </div>
      </ConfigProvider>
    </div>
  );
};

export default FormContainerSuccess;
